"use client";

import { useState, useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";
import Icon from "@/components/ui/AppIcon";

const testimonials = [
{
  id: 1,
  name: "Emily Richardson",
  location: "London, UK 🇬🇧",
  role: "Mum of 2",
  quote: "My daughter absolutely adores the ABC Animals book. She's been carrying it everywhere! The paper quality is outstanding and the illustrations are just magical.",
  rating: 5,
  image: "https://images.unsplash.com/photo-1714646793441-38c8f94141f8",
  alt: "Emily Richardson, a happy mother from London UK who purchased children's coloring books",
  bookBought: "ABC Wonderland"
},
{
  id: 2,
  name: "Marcus Thompson",
  location: "New York, USA 🇺🇸",
  role: "Dad & Educator",
  quote: "As a kindergarten teacher, I've tried dozens of workbooks. My Happy Place Digital is genuinely in a league of its own — educational, beautiful, and kids beg to use them.",
  rating: 5,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_13358a858-1767335246696.png",
  alt: "Marcus Thompson, kindergarten teacher and father from New York USA reviewing educational coloring books",
  bookBought: "Tracing & Writing Starter"
},
{
  id: 3,
  name: "Priya Nair",
  location: "Singapore 🇸🇬",
  role: "Mum of 3",
  quote: "Ordered for all three kids and they were fighting over who gets to color first! Fast shipping to Singapore, beautiful packaging. Will definitely order again!",
  rating: 5,
  image: "https://images.unsplash.com/photo-1730549026351-b53da72656ba",
  alt: "Priya Nair, mother of three from Singapore reviewing My Happy Place Digital children's books",
  bookBought: "Ocean World Set"
},
{
  id: 4,
  name: "Lars Andersen",
  location: "Oslo, Norway 🇳🇴",
  role: "Parent & Designer",
  quote: "The design sensibility here is extraordinary. These aren't generic coloring books — they're works of art that children can participate in. My son (5) has filled three already.",
  rating: 5,
  image: "https://images.unsplash.com/photo-1698236115739-2a7d0bae0ab5",
  alt: "Lars Andersen, parent and designer from Oslo Norway sharing review of children's coloring books",
  bookBought: "Magic & Fantasy Collection"
},
{
  id: 5,
  name: "Sophie Laurent",
  location: "Zurich, Switzerland 🇨🇭",
  role: "Montessori Teacher",
  quote: "I recommend these to all my students' parents. The Montessori approach is clearly evident — open-ended, beautiful, and developmentally appropriate. Exceptional quality.",
  rating: 5,
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_163c27203-1766492894344.png",
  alt: "Sophie Laurent, Montessori teacher from Zurich Switzerland endorsing My Happy Place Digital books",
  bookBought: "123 Rainbow Numbers"
}];


export default function TestimonialsSection() {
  const [current, setCurrent] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [direction, setDirection] = useState<"left" | "right">("right");
  const ref = useRef<HTMLDivElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const goTo = (index: number, dir: "left" | "right") => {
    if (isAnimating) return;
    setIsAnimating(true);
    setDirection(dir);
    setTimeout(() => {
      setCurrent(index);
      setIsAnimating(false);
    }, 350);
  };

  const next = () => goTo((current + 1) % testimonials.length, "right");
  const prev = () => goTo((current - 1 + testimonials.length) % testimonials.length, "left");

  useEffect(() => {
    intervalRef.current = setInterval(next, 5500);
    return () => {if (intervalRef.current) clearInterval(intervalRef.current);};
  }, [current]);

  const t = testimonials[current];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.remove("hidden-before-reveal");
        });
      },
      { threshold: 0.1 }
    );
    ref.current?.querySelectorAll(".reveal").forEach((el) => {
      el.classList.add("hidden-before-reveal");
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-24 bg-plum relative overflow-hidden">
      {/* BG decoration */}
      <div className="absolute top-0 left-0 w-96 h-96 rounded-full blur-3xl opacity-15" style={{ background: "var(--lavender)" }} />
      <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full blur-3xl opacity-10" style={{ background: "var(--peach)" }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full border border-white/5" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="reveal text-center mb-14">
          <span className="text-xs font-black tracking-widest uppercase text-white/60 bg-white/10 px-3 py-1 rounded-full">
            💬 Parent Stories
          </span>
          <h2 className="font-fraunces text-4xl md:text-5xl font-black text-white mt-4 leading-tight">
            Loved by Families
            <span className="italic text-lavender block">Worldwide</span>
          </h2>
        </div>

        {/* Main testimonial */}
        <div className="reveal grid md:grid-cols-12 gap-8 items-center max-w-5xl mx-auto">
          {/* Thumbnails — left */}
          <div className="hidden md:flex md:col-span-2 flex-col gap-3 items-center">
            {testimonials.filter((_, i) => i !== current).slice(0, 3).map((tt) =>
            <button
              key={tt.id}
              onClick={() => goTo(testimonials.findIndex((x) => x.id === tt.id), "right")}
              className="w-14 h-16 rounded-2xl overflow-hidden opacity-40 hover:opacity-80 transition-opacity border-2 border-white/20"
              aria-label={`View testimonial from ${tt.name}`}>
              
                <AppImage src={tt.image} alt={tt.alt} width={56} height={64} className="object-cover w-full h-full" />
              </button>
            )}
          </div>

          {/* Main image */}
          <div className="md:col-span-4 flex justify-center">
            <div
              className={`w-56 h-64 md:w-64 md:h-80 rounded-3xl overflow-hidden border-4 border-white/20 shadow-dreamy-xl transition-all duration-500 ${isAnimating ? "opacity-0 scale-95" : "opacity-100 scale-100"}`}>
              
              <AppImage
                src={t.image}
                alt={t.alt}
                width={256}
                height={320}
                className="object-cover w-full h-full" />
              
            </div>
          </div>

          {/* Quote */}
          <div className="md:col-span-6 md:pl-6">
            <div
              className={`transition-all duration-350 ${isAnimating ? `opacity-0 ${direction === "right" ? "translate-x-8" : "-translate-x-8"}` : "opacity-100 translate-x-0"}`}
              style={{ transitionDuration: "350ms" }}>
              
              <div className="text-4xl text-white/20 font-fraunces mb-4" aria-hidden="true">"</div>
              <blockquote className="font-fraunces text-xl md:text-2xl text-white font-light leading-relaxed italic mb-6">
                {t.quote}
              </blockquote>
              <div className="flex items-center gap-1 mb-4">
                {[...Array(t.rating)].map((_, i) =>
                <Icon key={i} name="StarIcon" size={18} variant="solid" className="text-gold" />
                )}
              </div>
              <div className="flex items-center gap-3">
                <div>
                  <div className="font-bold text-white text-base">{t.name}</div>
                  <div className="text-sm text-white/60">{t.role} · {t.location}</div>
                  <div className="text-xs text-lavender mt-1 font-medium">Purchased: {t.bookBought}</div>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex items-center gap-3 mt-8">
              <button
                onClick={prev}
                className="w-11 h-11 rounded-full border border-white/20 bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                aria-label="Previous testimonial">
                
                <Icon name="ArrowLeftIcon" size={18} className="text-white" />
              </button>
              <button
                onClick={next}
                className="w-11 h-11 rounded-full bg-lavender flex items-center justify-center hover:bg-lavender/80 transition-colors"
                aria-label="Next testimonial">
                
                <Icon name="ArrowRightIcon" size={18} className="text-plum" />
              </button>
              <div className="flex gap-2 ml-2">
                {testimonials.map((_, i) =>
                <button
                  key={i}
                  onClick={() => goTo(i, i > current ? "right" : "left")}
                  className={`h-1.5 rounded-full transition-all duration-300 ${i === current ? "w-6 bg-lavender" : "w-1.5 bg-white/30"}`}
                  aria-label={`Go to testimonial ${i + 1}`} />

                )}
              </div>
              <span className="ml-auto text-xs text-white/40 font-mono">
                {String(current + 1).padStart(2, "0")} / {String(testimonials.length).padStart(2, "0")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>);

}